import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { TrendingUp, Users, Target, Activity, RefreshCw, Plus, Trash2, User, Building2, Clock, AlertTriangle, TrendingDown, ArrowRight, Radar, ShieldCheck, Search, X, ChevronDown, Globe } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, CircleMarker, Tooltip } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import DailyInsight from "../components/DailyInsight";
import HealthGauge from "../components/HealthGauge";
import { useLanguage } from "../i18n/LanguageContext";
import { t as tRaw } from "../i18n/translations";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// ─── Estrai tag di motivi principali dalle note del fornitore ────────────
function parseSupplierReasons(notes, lang = "it") {
  if (!notes) return [];
  const src = notes.toLowerCase();
  const rules = [
    { kw: ["ritard", "otd", "consegn", "slittament"], key: "dash.reason.delivery_delay" },
    { kw: ["prezz", "aument", "rincaro", "costi", "+%"],  key: "dash.reason.price_increase" },
    { kw: ["qualit", "difett", "non conform", "reclam"],  key: "dash.reason.quality" },
    { kw: ["lead time"],                                   key: "dash.reason.lead_time" },
    { kw: ["sla"],                                         key: "dash.reason.sla" },
    { kw: ["congestion", "hub", "logistic"],               key: "dash.reason.logistics" },
    { kw: ["rinegoz", "contratto"],                        key: "dash.reason.renegotiation" },
  ];
  const out = [];
  for (const r of rules) {
    if (r.kw.some(k => src.includes(k))) out.push(tRaw(r.key, lang));
  }
  return out.slice(0, 4);
}

// ─── Deterministic company health score from id/name (30-95 range) ─────────
function scoreFor(business) {
  const key = business?.id || business?.name || "";
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return 40 + (h % 56); // 40..95
}
function prevScoreFor(business) {
  const key = (business?.id || business?.name || "") + "_prev";
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  const raw = 38 + (h % 58);
  return Math.max(30, Math.min(97, raw));
}

// ─── Updates column (People / Company) ────────────────────────────────────
function UpdatesColumn({ title, items, accent, emptyLabel, renderMeta }) {
  const Icon = accent === "#0891B2" ? User : Building2;
  return (
    <div className="bg-[#0D0F12] border border-white/[0.04] overflow-hidden">
      <div className="flex items-center gap-2 px-5 py-3 border-b border-white/[0.04]">
        <div className="w-7 h-7 flex items-center justify-center rounded-lg" style={{ backgroundColor: `${accent}18`, border: `1px solid ${accent}40` }}>
          <Icon className="w-3.5 h-3.5" style={{ color: accent }} />
        </div>
        <p className="text-sm text-white font-semibold tracking-[0.12em] uppercase">{title}</p>
        <span className="ml-auto text-[10px] text-[#A0AEC0]">{items.length} recenti</span>
      </div>
      <div className="divide-y divide-[#1F242D]">
        {items.length === 0 ? (
          <div className="p-6 text-xs text-[#A0AEC0] text-center">{emptyLabel}</div>
        ) : items.map((item, i) => (
          <div key={item.id || `${item.name || item.nome || "?"}-${i}`} className="px-5 py-3 flex items-start gap-3 hover:bg-[#1A1D24] transition-colors">
            <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
              style={{ backgroundColor: `${accent}15`, border: `1px solid ${accent}30`, color: accent, fontFamily: "JetBrains Mono, monospace", fontSize: 11, fontWeight: 700 }}>
              {(item.name || item.nome || "?").slice(0, 2).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-white font-medium truncate">{item.name || item.nome || "—"}</p>
              <p className="text-[11px] text-[#CBD5E1] truncate">{renderMeta(item) || "—"}</p>
            </div>
            <div className="flex items-center gap-1 text-[10px] text-[#A0AEC0] flex-shrink-0 mt-1">
              <Clock className="w-3 h-3" />
              {(item.updated_at || item.created_at || "").slice(0, 10) || "—"}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


export default function DashboardPage() {
  const { t, lang } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [editRows, setEditRows] = useState([]);
  const [techPatentCount, setTechPatentCount] = useState(0);
  const [peopleCount, setPeopleCount] = useState(0);
  const [businessesCount, setBusinessesCount] = useState(0);
  const [eventsFairsCount, setEventsFairsCount] = useState(0);
  const [allBusinesses, setAllBusinesses] = useState([]);
  const [companyQuery, setCompanyQuery] = useState("");
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const [expandedSupplierId, setExpandedSupplierId] = useState(null);
  const [recentProfiles, setRecentProfiles] = useState([]);
  const [recentBusinesses, setRecentBusinesses] = useState([]);
  const [decliningSuppliers, setDecliningSuppliers] = useState([]);
  const [improvingSuppliers, setImprovingSuppliers] = useState([]);
  const [healthGauges, setHealthGauges] = useState({ client_health: 0, client_health_prev: 0, client_delta: 0, client_count: 0, supplier_health: 0, supplier_health_prev: 0, supplier_delta: 0, supplier_count: 0 });
  const [healthCompanies, setHealthCompanies] = useState({ clients: [], suppliers: [] });
  const [clientMarkerId, setClientMarkerId] = useState("");
  const [supplierMarkerId, setSupplierMarkerId] = useState("");

  // Ricarica gauge quando cambiano i filtri
  useEffect(() => {
    const params = new URLSearchParams();
    if (clientMarkerId) params.set("client_marker_id", clientMarkerId);
    if (supplierMarkerId) params.set("supplier_marker_id", supplierMarkerId);
    const qs = params.toString();
    axios.get(`${API}/dashboard/health-gauges${qs ? `?${qs}` : ""}`, { withCredentials: true })
      .then(r => setHealthGauges(r.data || { client_health: 0, client_health_prev: 0, client_delta: 0, client_count: 0, supplier_health: 0, supplier_health_prev: 0, supplier_delta: 0, supplier_count: 0 }))
      .catch(() => {});
  }, [clientMarkerId, supplierMarkerId]);

  useEffect(() => {
    loadMetrics();
    // Tech + Patent
    Promise.all([
      axios.get(`${API}/tech/patents`, { withCredentials: true }).catch(() => ({ data: [] })),
      axios.get(`${API}/tech/technologies`, { withCredentials: true }).catch(() => ({ data: [] })),
    ]).then(([patents, techs]) => {
      const techItems = techs.data.reduce((s, c) => s + (c.technologies?.length || 0), 0);
      setTechPatentCount(patents.data.length + techItems);
    });
    // Recent People (profiles) + Company (businesses)
    axios.get(`${API}/sales/profiles`, { withCredentials: true })
      .then(r => {
        const list = r.data || [];
        setPeopleCount(list.length);
        const sorted = [...list].sort((a, b) =>
          (b.updated_at || b.created_at || "").localeCompare(a.updated_at || a.created_at || "")
        );
        setRecentProfiles(sorted.slice(0, 5));
      }).catch(() => {});
    axios.get(`${API}/sales/businesses`, { withCredentials: true })
      .then(r => {
        const list = r.data || [];
        setBusinessesCount(list.length);
        setAllBusinesses(list);
        const sorted = [...list].sort((a, b) =>
          (b.updated_at || b.created_at || "").localeCompare(a.updated_at || a.created_at || "")
        );
        setRecentBusinesses(sorted.slice(0, 5));
      }).catch(() => {});
    // Event and Fairs count (tradeshows + events)
    Promise.all([
      axios.get(`${API}/network/tradeshows`, { withCredentials: true }).catch(() => ({ data: [] })),
      axios.get(`${API}/network/events`, { withCredentials: true }).catch(() => ({ data: [] })),
    ]).then(([ts, ev]) => {
      setEventsFairsCount((ts.data?.length || 0) + (ev.data?.length || 0));
    });
    // Declining + improving suppliers (early warning) — single aggregated call
    axios.get(`${API}/dashboard/supplier-alerts`, { withCredentials: true })
      .then(r => {
        setDecliningSuppliers(r.data?.declining || []);
        setImprovingSuppliers(r.data?.improving || []);
      }).catch(() => {});
    // Health gauges (client + supplier salute aggregata)
    axios.get(`${API}/dashboard/health-gauges`, { withCredentials: true })
      .then(r => setHealthGauges(r.data || { client_health: 0, client_health_prev: 0, client_delta: 0, client_count: 0, supplier_health: 0, supplier_health_prev: 0, supplier_delta: 0, supplier_count: 0 }))
      .catch(() => {});
    // Elenco aziende per i filtri dei cruscotti di salute
    axios.get(`${API}/dashboard/health-companies`, { withCredentials: true })
      .then(r => setHealthCompanies(r.data || { clients: [], suppliers: [] }))
      .catch(() => {});
  }, []);

  const loadMetrics = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/dashboard/metrics`, { withCredentials: true });
      setMetrics(data);
      setEditRows(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const saveMetrics = async () => {
    await axios.post(`${API}/dashboard/metrics`, editRows, { withCredentials: true });
    await loadMetrics();
    setEditMode(false);
  };

  const addRow = () => setEditRows(prev => [...prev, { label: "", contacts: 0, conversions: 0, visits: 0, category: "monthly" }]);
  const removeRow = (i) => setEditRows(prev => prev.filter((_, idx) => idx !== i));
  const updateRow = (i, field, value) => setEditRows(prev => prev.map((r, idx) => idx === i ? { ...r, [field]: field === "label" || field === "category" ? value : Number(value) } : r));

  const totalContacts = metrics.reduce((s, m) => s + (m.contacts || 0), 0);
  const totalConversions = metrics.reduce((s, m) => s + (m.conversions || 0), 0);
  const totalVisits = metrics.reduce((s, m) => s + (m.visits || 0), 0);

  return (
    <div className="p-6 sm:p-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white tracking-tight" style={{ fontFamily: "Chivo, sans-serif" }}>{t("dash.title")}</h1>
          <p className="text-xs text-[#A0AEC0] mt-1 tracking-widest uppercase">{user?.client_name} &mdash; {t("dash.overview")}</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="refresh-dashboard" onClick={loadMetrics} className="flex items-center gap-2 bg-[#1F242D] text-white border border-white/[0.04] px-3 py-2 text-xs hover:bg-[#30363D] transition-colors">
            <RefreshCw className="w-3.5 h-3.5" /> {t("action.refresh")}
          </button>
          <button data-testid="edit-metrics-button" onClick={() => { setEditMode(!editMode); setEditRows(metrics); }} className="flex items-center gap-2 bg-[#1F242D] text-white border border-white/[0.04] px-3 py-2 text-xs hover:bg-[#30363D] transition-colors">
            {t("action.edit_data")}
          </button>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════
         OPENPOLIS-STYLE OVERVIEW — 3-column hero panel
         Structure inspired by openpolis "Dove vanno i nostri soldi" — 
         adapted with data already available in-platform (businesses, people, 
         suppliers health, monthly signals, alerts).
         ═══════════════════════════════════════════════════════════════════ */}
      <OpenpolisOverview
        totalBusinesses={businessesCount}
        totalPeople={peopleCount}
        totalPatents={techPatentCount}
        totalEvents={eventsFairsCount}
        healthGauges={healthGauges}
        recentBusinesses={recentBusinesses}
        decliningSuppliers={decliningSuppliers}
        metrics={metrics}
        onNav={(p) => navigate(p)}
      />

      {/* Overview: Statistiche + Early Warning / Positive Signal */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 items-start">
        {/* — Sinistra: contenitore unico con 4 metriche complessive — */}
        <div className="lg:col-span-2 bg-[#0D0F12] border border-white/[0.04] overflow-hidden self-stretch flex flex-col" style={{ borderRadius: "2px" }}>
          <div className="px-4 py-2.5">
            <p className="text-[10px] tracking-[0.18em] uppercase text-[#00E5FF] font-bold">{t("dash.overview.title")}</p>
            <p className="text-[9px] text-[#A0AEC0] tracking-widest uppercase mt-0.5">{t("dash.overview.subtitle")}</p>
          </div>
          <div className="flex flex-col flex-1">
            {[
              { label: t("dash.overview.people_list"),     value: peopleCount,       icon: Users,      color: "#0891B2", onClick: () => navigate("/sales/people-list") },
              { label: t("dash.overview.business_list"),   value: businessesCount,   icon: Building2,  color: "#059669", onClick: () => navigate("/sales/businesses") },
              { label: t("dash.overview.business_asset"),  value: techPatentCount,   icon: TrendingUp, color: "#7C3AED", onClick: () => navigate("/tech/monitor") },
              { label: t("dash.overview.events_fairs"),    value: eventsFairsCount,  icon: Activity,   color: "#EA580C", onClick: () => navigate("/network/events") },
            ].map((kpi, i) => (
              <button
                key={kpi.label}
                data-testid={`kpi-card-${i}`}
                onClick={kpi.onClick}
                className="text-left px-4 py-2.5 flex items-center gap-3 hover:bg-[#12161C] transition-colors"
              >
                <div
                  className="w-7 h-7 flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: `${kpi.color}18` }}
                >
                  <kpi.icon className="w-3.5 h-3.5" style={{ color: kpi.color }} />
                </div>
                <p className="flex-1 text-[11px] text-white font-semibold tracking-[0.14em] uppercase leading-tight">{kpi.label}</p>
                <p className="text-lg font-semibold tabular-nums" style={{ fontFamily: "JetBrains Mono, monospace", color: kpi.color }}>{kpi.value}</p>
              </button>
            ))}
          </div>
        </div>

        {/* — Destra: Centro di Controllo Supply Chain — */}
        <div className="lg:col-span-3 self-stretch bg-[#0D0F12] border border-white/[0.04] overflow-hidden flex flex-col" style={{ borderRadius: "2px" }}>
          {/* Header */}
          <div className="px-5 py-3 flex items-center gap-3 flex-wrap">
            <div className="w-9 h-9 flex items-center justify-center bg-[#00E5FF]/12" style={{ borderRadius: "2px" }}>
              <Radar className="w-4 h-4 text-[#00E5FF]" strokeWidth={2} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] tracking-[0.22em] uppercase text-[#00E5FF] font-bold">{t("dash.supply.title")}</p>
              <p className="text-[10px] tracking-widest uppercase text-[#A0AEC0] mt-0.5">{t("dash.supply.subtitle")}</p>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-[#FF3B30]/12" style={{ borderRadius: "2px" }}>
                <TrendingDown className="w-3.5 h-3.5 text-[#FF3B30]" />
                <span className="text-[10px] tracking-widest uppercase text-[#FF3B30] font-semibold">{t("dash.supply.declining_short")}</span>
                <span className="text-sm font-bold tabular-nums text-[#FF3B30]" style={{ fontFamily: "Chivo, sans-serif" }}>{decliningSuppliers.length}</span>
              </div>
              <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-[#16C784]/12" style={{ borderRadius: "2px" }}>
                <TrendingUp className="w-3.5 h-3.5 text-[#16C784]" />
                <span className="text-[10px] tracking-widest uppercase text-[#16C784] font-semibold">{t("dash.supply.improving_short")}</span>
                <span className="text-sm font-bold tabular-nums text-[#16C784]" style={{ fontFamily: "Chivo, sans-serif" }}>{improvingSuppliers.length}</span>
              </div>
              <button
                data-testid="open-warroom-supply-chain"
                onClick={() => navigate("/warroom")}
                className="flex items-center gap-1.5 px-3 py-2 bg-[#00E5FF]/12 hover:bg-[#00E5FF]/20 text-[#00E5FF] text-[10px] font-semibold tracking-widest uppercase transition-colors"
                style={{ borderRadius: "2px" }}
              >
                {t("dash.supply.open_warroom")} <ArrowRight className="w-3 h-3" />
              </button>
            </div>
          </div>

          {/* Corpo: due colonne */}
          <div className="grid grid-cols-1 md:grid-cols-2 flex-1">
            {/* Colonna: Early Warning */}
            <div className="p-4 flex flex-col gap-2 border-r border-white/[0.04]">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-3.5 h-3.5 text-[#FF3B30]" strokeWidth={2.25} />
                <p className="text-[10px] tracking-[0.2em] uppercase text-[#FF3B30] font-bold">{t("dash.supply.early_warning")}</p>
                <span className="ml-auto text-[10px] text-[#6B7280] tracking-widest">{t("dash.supply.score_minus_10")}</span>
              </div>
              {decliningSuppliers.length === 0 ? (
                <div className="flex-1 flex flex-col items-center justify-center py-6 text-center">
                  <p className="text-[10px] tracking-widest uppercase text-[#6B7280]">{t("dash.supply.no_critical")}</p>
                  <p className="text-[11px] text-[#94A3B8] mt-1">{t("dash.supply.stable_period")}</p>
                </div>
              ) : (
                <div className="flex flex-col gap-1.5 mt-1">
                  {decliningSuppliers.slice(0, 4).map(s => {
                    const isOpen = expandedSupplierId === s.marker_id;
                    const reasons = parseSupplierReasons(s.notes, lang);
                    return (
                      <div key={s.marker_id} className="bg-[#0A0C10]" style={{ borderRadius: "2px" }}>
                        <button
                          data-testid="declining-supplier-card"
                          onClick={() => setExpandedSupplierId(isOpen ? null : s.marker_id)}
                          className="w-full px-3 py-2 flex items-center gap-3 hover:bg-[#12161C] transition-colors text-left"
                        >
                          <div className="min-w-0 flex-1">
                            <p className="text-[13px] text-white font-semibold truncate" title={s.name}>{s.name}</p>
                            <p className="text-[10px] text-[#A0AEC0] truncate">{s.city}{s.sector ? ` · ${s.sector}` : ""}</p>
                          </div>
                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            <span className="text-base font-bold text-[#FF3B30] tabular-nums" style={{ fontFamily: "Chivo, sans-serif" }}>{s.score_current}</span>
                            <div className="flex items-center gap-0.5 px-1.5 py-0.5 bg-[#FF3B30]/20">
                              <TrendingDown className="w-3 h-3 text-[#FF3B30]" />
                              <span className="text-[10px] font-bold tabular-nums text-[#FF3B30]">{s.delta}</span>
                            </div>
                            <ChevronDown className={`w-3.5 h-3.5 text-[#94A3B8] transition-transform ${isOpen ? "rotate-180" : ""}`} />
                          </div>
                        </button>
                        {isOpen && (
                          <div data-testid="supplier-reasons" className="px-3 pb-3 pt-1 border-t border-white/[0.04]">
                            {reasons.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2 mb-2">
                                {reasons.map(r => (
                                  <span key={r} className="text-[9px] tracking-wider uppercase text-[#FF3B30] bg-[#FF3B30]/12 px-1.5 py-0.5" style={{ borderRadius: "2px", border: "1px solid #FF3B3055" }}>{r}</span>
                                ))}
                              </div>
                            )}
                            <p className="text-[11px] text-[#CBD5E1] leading-relaxed">{s.notes || t("dash.supply.no_notes")}</p>
                            <button
                              onClick={(e) => { e.stopPropagation(); navigate("/warroom"); }}
                              className="mt-2 text-[10px] text-[#00E5FF] hover:text-white tracking-widest uppercase flex items-center gap-1"
                            >
                              {t("dash.supply.open_warroom_card")} <ArrowRight className="w-3 h-3" />
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Colonna: Positive Signal */}
            <div className="p-4 flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-3.5 h-3.5 text-[#16C784]" strokeWidth={2.25} />
                <p className="text-[10px] tracking-[0.2em] uppercase text-[#16C784] font-bold">{t("dash.supply.positive_signal")}</p>
                <span className="ml-auto text-[10px] text-[#6B7280] tracking-widest">{t("dash.supply.score_plus_10")}</span>
              </div>
              {improvingSuppliers.length === 0 ? (
                <div className="flex-1 flex flex-col items-center justify-center py-6 text-center">
                  <p className="text-[10px] tracking-widest uppercase text-[#6B7280]">{t("dash.supply.no_improvement")}</p>
                  <p className="text-[11px] text-[#94A3B8] mt-1">{t("dash.supply.radar_monitoring")}</p>
                </div>
              ) : (
                <div className="flex flex-col gap-1.5 mt-1">
                  {improvingSuppliers.slice(0, 4).map(s => (
                    <div key={s.marker_id} data-testid="improving-supplier-card"
                      className="bg-[#0A0C10] px-3 py-2 flex items-center gap-3 cursor-pointer hover:bg-[#12161C] transition-colors"
                      onClick={() => navigate("/warroom")}>
                      <div className="min-w-0 flex-1">
                        <p className="text-[13px] text-white font-semibold truncate" title={s.name}>{s.name}</p>
                        <p className="text-[10px] text-[#A0AEC0] truncate">{s.city}{s.sector ? ` · ${s.sector}` : ""}</p>
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <span className="text-base font-bold text-[#16C784] tabular-nums" style={{ fontFamily: "Chivo, sans-serif" }}>{s.score_current}</span>
                        <div className="flex items-center gap-0.5 px-1.5 py-0.5 bg-[#16C784]/20">
                          <TrendingUp className="w-3 h-3 text-[#16C784]" />
                          <span className="text-[10px] font-bold tabular-nums text-[#16C784]">+{s.delta}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Company Health Check + Insight del Giorno affiancati (2 + 3) */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 items-stretch">
        <div className="lg:col-span-2">
          <CompanyHealthCheck
            allBusinesses={allBusinesses}
            companyQuery={companyQuery}
            setCompanyQuery={setCompanyQuery}
            selectedCompany={selectedCompany}
            setSelectedCompany={setSelectedCompany}
            showDropdown={showCompanyDropdown}
            setShowDropdown={setShowCompanyDropdown}
            lang={lang}
          />
        </div>
        <div className="lg:col-span-3">
          <DailyInsight orientation="horizontal" />
        </div>
      </div>

      {/* Row 2b: Ultimi Aggiornamenti (People + Company) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <UpdatesColumn
          title={t("dash.recent_people")}
          accent="#0891B2"
          items={recentProfiles}
          emptyLabel={t("dash.no_contacts_updated")}
          renderMeta={(p) => [p.role, p.company].filter(Boolean).join(" · ")}
        />
        <UpdatesColumn
          title={t("dash.recent_company")}
          accent="#059669"
          items={recentBusinesses}
          emptyLabel={t("dash.no_companies_updated")}
          renderMeta={(b) => [b.sector || b.industry, b.location].filter(Boolean).join(" · ")}
        />
      </div>

      {/* Edit Modal */}
      {editMode && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#0D0F12] border border-white/[0.04] w-full max-w-3xl max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.04]">
              <p className="text-sm font-semibold text-white">Edit Metrics Data</p>
              <button onClick={() => setEditMode(false)} className="text-[#A0AEC0] hover:text-white text-xl">&times;</button>
            </div>
            <div className="overflow-auto flex-1 p-5">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-[#A0AEC0] tracking-widest uppercase border-b border-white/[0.04]">
                    <th className="text-left pb-2 pr-3">Label</th>
                    <th className="text-left pb-2 pr-3">ID Profile</th>
                    <th className="text-left pb-2 pr-3">ID Business</th>
                    <th className="text-left pb-2 pr-3">Intelligence</th>
                    <th className="pb-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {editRows.map((row, i) => (
                    <tr key={row.id || row.label || `row-${i}`} className="border-b border-white/[0.04]">
                      {["label", "contacts", "conversions", "visits"].map(f => (
                        <td key={f} className="py-2 pr-3">
                          <input value={row[f] || ""} onChange={e => updateRow(i, f, e.target.value)}
                            className="w-full bg-[#050505] border border-white/[0.04] text-white px-2 py-1 text-xs focus:outline-none focus:border-[#00E5FF]" />
                        </td>
                      ))}
                      <td className="py-2">
                        <button onClick={() => removeRow(i)} className="text-[#FF3B30] hover:text-[#FF3B30]/70"><Trash2 className="w-3.5 h-3.5" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <button onClick={addRow} className="mt-3 flex items-center gap-1 text-[#00E5FF] text-xs hover:underline">
                <Plus className="w-3 h-3" /> Add Row
              </button>
            </div>
            <div className="px-5 py-4 border-t border-white/[0.04] flex gap-3 justify-end">
              <button onClick={() => setEditMode(false)} className="px-4 py-2 text-xs bg-[#1F242D] text-white border border-white/[0.04] hover:bg-[#30363D]">Cancel</button>
              <button data-testid="save-metrics-button" onClick={saveMetrics} className="px-4 py-2 text-xs bg-[#00E5FF] text-black font-semibold hover:bg-[#00B3CC]">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Company Health Check ─────────────────────────────────────────────────
function CompanyHealthCheck({ allBusinesses, companyQuery, setCompanyQuery, selectedCompany, setSelectedCompany, showDropdown, setShowDropdown, lang }) {
  const total = allBusinesses.length;
  const aggregate = React.useMemo(() => {
    if (!total) return { current: 0, previous: 0 };
    let curr = 0, prev = 0;
    for (const b of allBusinesses) { curr += scoreFor(b); prev += prevScoreFor(b); }
    return { current: curr / total, previous: prev / total };
  }, [allBusinesses, total]);

  const filteredSuggestions = React.useMemo(() => {
    if (!companyQuery.trim()) return [];
    const q = companyQuery.trim().toLowerCase();
    return allBusinesses
      .filter(b => (b.name || "").toLowerCase().includes(q))
      .slice(0, 8);
  }, [allBusinesses, companyQuery]);

  const displayScore = selectedCompany ? scoreFor(selectedCompany) : aggregate.current;
  const displayPrev = selectedCompany ? prevScoreFor(selectedCompany) : aggregate.previous;
  const delta = Math.round(displayScore - displayPrev);
  const isAggregate = !selectedCompany;

  const pickCompany = (b) => {
    setSelectedCompany(b);
    setCompanyQuery(b.name);
    setShowDropdown(false);
  };
  const clearCompany = () => {
    setSelectedCompany(null);
    setCompanyQuery("");
    setShowDropdown(false);
  };

  return (
    <div
      data-testid="company-health-check"
      className="bg-[#0D0F12] border border-white/[0.04] overflow-hidden"
      style={{ borderRadius: "2px" }}
    >
      {/* Header */}
      <div className="flex items-center justify-between gap-3 px-6 py-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 flex items-center justify-center bg-[#00E5FF]/12" style={{ borderRadius: "2px" }}>
            <ShieldCheck className="w-4 h-4 text-[#00E5FF]" strokeWidth={2} />
          </div>
          <div>
            <p className="text-[11px] tracking-[0.22em] uppercase text-[#00E5FF] font-bold">{tRaw("dash.health.title", lang)}</p>
            <p className="text-[10px] text-[#A0AEC0] tracking-wide mt-0.5">
              {isAggregate
                ? tRaw("dash.health.aggregate_sub", lang).replace("{n}", total)
                : tRaw("dash.health.single_sub", lang)}
            </p>
          </div>
        </div>
        {/* Legenda soglie */}
        <div className="flex items-center gap-3 text-[9px] tracking-widest uppercase text-[#6B7280]">
          <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-[#FF1744]" style={{ boxShadow: "0 0 6px #FF1744" }} />{tRaw("dash.health.legend_low", lang)}</span>
          <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-[#FFB800]" style={{ boxShadow: "0 0 6px #FFB800" }} />{tRaw("dash.health.legend_mid", lang)}</span>
          <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-[#00E676]" style={{ boxShadow: "0 0 6px #00E676" }} />{tRaw("dash.health.legend_high", lang)}</span>
        </div>
      </div>

      {/* Ricerca */}
      <div className="px-6 pb-4">
        <div className="relative max-w-xl">
          <Search className="w-4 h-4 text-[#6B7280] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            data-testid="company-health-search"
            type="text"
            value={companyQuery}
            onChange={(e) => { setCompanyQuery(e.target.value); setShowDropdown(true); if (selectedCompany && !e.target.value) setSelectedCompany(null); }}
            onFocus={() => setShowDropdown(true)}
            onBlur={() => setTimeout(() => setShowDropdown(false), 180)}
            placeholder={tRaw("dash.health.search_placeholder", lang)}
            className="w-full pl-10 pr-10 py-2.5 bg-[#0A0C10] border border-white/[0.06] text-white text-sm placeholder:text-[#6B7280] focus:outline-none focus:border-[#00E5FF]"
            style={{ borderRadius: "2px" }}
          />
          {(companyQuery || selectedCompany) && (
            <button
              data-testid="company-health-clear"
              onClick={clearCompany}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A0AEC0] hover:text-white"
              aria-label="clear"
            >
              <X className="w-4 h-4" />
            </button>
          )}
          {/* Dropdown suggerimenti */}
          {showDropdown && filteredSuggestions.length > 0 && (
            <div className="absolute z-20 mt-1 w-full bg-white max-h-64 overflow-y-auto shadow-xl" style={{ borderRadius: "2px", boxShadow: "0 4px 16px rgba(0,0,0,0.18)" }}>
              {filteredSuggestions.map(b => (
                <button
                  key={b.id}
                  data-testid="company-health-suggestion"
                  onMouseDown={(e) => { e.preventDefault(); pickCompany(b); }}
                  className="w-full text-left px-3 py-2 hover:bg-slate-100 transition-colors flex items-center gap-2 border-b border-slate-100 last:border-0"
                >
                  <Building2 className="w-3.5 h-3.5 text-[#059669] flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-[12px] truncate font-semibold" style={{ color: "#0D1B3D" }}>{b.name}</p>
                    <p className="text-[10px] text-slate-500 truncate">{[b.sector || b.industry, b.hq || b.location].filter(Boolean).join(" · ")}</p>
                  </div>
                  <span className="text-[11px] font-bold tabular-nums" style={{ color: "#0D1B3D" }}>{scoreFor(b)}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Corpo: Score badge + gauge */}
      <div className="px-6 pb-6 flex flex-col gap-4">
        {/* Riga: card azienda (sinistra) + score badge (destra), altezza fissa */}
        <div className="flex items-stretch justify-between gap-3">
          <div
            className="flex-1 min-w-0 flex items-center gap-3 px-4 py-3 bg-white shadow-sm"
            style={{ borderRadius: "2px", boxShadow: "0 1px 4px rgba(0,0,0,0.08)" }}
          >
            <div
              className="w-9 h-9 flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: selectedCompany ? "#0596691F" : "#00E5FF1F", borderRadius: "2px" }}
            >
              {selectedCompany
                ? <Building2 className="w-4 h-4 text-[#059669]" />
                : <ShieldCheck className="w-4 h-4 text-[#0891B2]" />}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold truncate" style={{ color: "#0D1B3D", fontFamily: "Chivo, sans-serif" }}>
                {selectedCompany ? selectedCompany.name : tRaw("dash.health.aggregate", lang)}
              </p>
              <p className="text-[10px] text-slate-500 truncate">
                {selectedCompany
                  ? [selectedCompany.sector || selectedCompany.industry, selectedCompany.hq || selectedCompany.location, selectedCompany.employees ? `${selectedCompany.employees} ${tRaw("dash.health.employees_short", lang)}` : null].filter(Boolean).join(" · ")
                  : tRaw("dash.health.tracked", lang).replace("{n}", total)}
              </p>
            </div>
            <div className="text-right flex-shrink-0">
              <p className="text-[9px] tracking-widest uppercase text-slate-500 font-semibold">{tRaw("dash.health.delta_3m", lang)}</p>
              <p className={`text-sm font-bold tabular-nums ${delta >= 0 ? "text-[#059669]" : "text-[#DC2626]"}`}>{delta >= 0 ? "+" : ""}{delta}</p>
            </div>
          </div>
          <div
            className="flex items-baseline gap-1 px-4 py-3 bg-white shadow-sm flex-shrink-0"
            style={{ borderRadius: "2px", boxShadow: "0 1px 4px rgba(0,0,0,0.08)" }}
          >
            <span className="text-[9px] tracking-[0.2em] uppercase text-slate-500 font-semibold mr-1">{tRaw("dash.health.score", lang)}</span>
            <span className="tabular-nums font-bold leading-none" style={{ fontFamily: "Chivo, sans-serif", fontSize: 22, color: "#0D1B3D" }}>
              {Math.round(displayScore)}
            </span>
            <span className="text-sm font-semibold" style={{ color: "#0D1B3D", opacity: 0.55 }}>/100</span>
          </div>
        </div>
        <TrafficLight score={displayScore} previous={displayPrev} lang={lang} />
      </div>
    </div>
  );
}

// ─── Traffic Light orizzontale — minimal ma catchy (stile pit-lane) ──────
function TrafficLightLamp({ id, color, on }) {
  return (
    <div
      data-testid={`traffic-light-${id}`}
      className="flex-1 h-10 transition-all duration-300"
      style={{
        borderRadius: "2px",
        backgroundColor: on ? color : "#E5E7EB",
        opacity: on ? 1 : 0.4,
      }}
    />
  );
}

function TrafficLight({ score, previous, lang }) {
  const s = Math.max(0, Math.min(100, Math.round(score)));
  const active = s < 40 ? "red" : s < 70 ? "amber" : "green";
  const delta = previous != null ? Math.round(score - previous) : 0;
  const statusLabel = active === "red"
    ? tRaw("dash.tl.needs_work", lang)
    : active === "amber"
      ? tRaw("dash.tl.fair", lang)
      : tRaw("dash.tl.optimal", lang);
  const statusColor = active === "red" ? "#FF1744" : active === "amber" ? "#FFB800" : "#00E676";

  return (
    <div className="flex flex-col items-center py-3 w-full" data-testid="company-traffic-light">
      {/* Semaforo pit-lane chiaro & minimal */}
      <div
        className="flex items-center gap-3 w-full max-w-md px-4 py-3 bg-white"
        style={{
          borderRadius: "2px",
          boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
        }}
      >
        <TrafficLightLamp id="red"   color="#FF1744" on={active === "red"} />
        <TrafficLightLamp id="amber" color="#FFB800" on={active === "amber"} />
        <TrafficLightLamp id="green" color="#00E676" on={active === "green"} />
      </div>

      {/* Badge status + delta */}
      <div className="flex items-center gap-3 mt-4 flex-wrap justify-center">
        <span
          data-testid="traffic-light-status"
          className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] font-bold tracking-[0.2em] uppercase"
          style={{ color: statusColor, backgroundColor: `${statusColor}18`, border: `1px solid ${statusColor}55`, borderRadius: "2px" }}
        >
          <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: statusColor, boxShadow: `0 0 6px ${statusColor}` }} />
          {statusLabel}
        </span>
        {previous != null && (
          <span
            data-testid="traffic-light-delta"
            className="inline-flex items-center gap-1 text-[10px] font-medium tabular-nums tracking-wide"
            style={{ color: delta > 0 ? "#00E676" : delta < 0 ? "#FF1744" : "#6B7280" }}
          >
            {delta > 0 ? "+" : ""}{delta}
            <span className="text-[#6B7280] font-normal ml-0.5">
              {tRaw("dash.tl.vs_last", lang)}
            </span>
          </span>
        )}
      </div>
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════════════
   Openpolis-style Overview (3-column hero panel)
   ═══════════════════════════════════════════════════════════════════ */
function OpenpolisOverview({ totalBusinesses, totalPeople, totalPatents, totalEvents, healthGauges, recentBusinesses, decliningSuppliers, metrics, onNav }) {
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedCountry, setSelectedCountry] = useState("");
  const [availableCountries, setAvailableCountries] = useState([]);
  const totalAssets = totalBusinesses + totalPeople + totalPatents + totalEvents;
  const dist = [
    { label: "Aziende",        value: totalBusinesses, color: "#059669" },
    { label: "Contatti",       value: totalPeople,     color: "#0891B2" },
    { label: "Asset R&D",      value: totalPatents,    color: "#7C3AED" },
    { label: "Eventi & Fiere", value: totalEvents,     color: "#EA580C" },
  ];
  const distTotal = dist.reduce((s, d) => s + d.value, 0) || 1;
  // build cumulative for donut
  let acc = 0;
  const donut = dist.map(d => {
    const start = acc / distTotal;
    acc += d.value;
    const end = acc / distTotal;
    return { ...d, start, end, pct: (d.value / distTotal) * 100 };
  });

  // Monthly signal (uses metrics.contacts as proxy)
  const monthlyRows = (metrics || []).slice(0, 8).map(m => ({
    label: m.label || "—",
    value: (m.contacts || 0) + (m.conversions || 0),
  }));
  const maxMonthly = Math.max(1, ...monthlyRows.map(r => r.value));

  const now = new Date();
  const lastUpdated = now.toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" });

  // load list of countries for the filter dropdown
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/dashboard/entity-density`, { withCredentials: true })
      .then(r => setAvailableCountries(r.data?.rows || []))
      .catch(() => setAvailableCountries([]));
  }, []);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
      {/* LEFT column — big KPI + donut + top 5 */}
      <div className="lg:col-span-3 bg-[#0D0F12] border border-white/[0.04] p-5" style={{ borderRadius: "2px" }} data-testid="openpolis-left">
        <p className="text-[10px] tracking-[0.22em] uppercase text-[#00E5FF] font-bold">Asset Portfolio Attivo</p>
        <p className="text-[10px] text-white/70 mt-0.5">Dati aggiornati al {lastUpdated}</p>
        <p className="text-4xl font-bold text-white tabular-nums mt-4" data-testid="op-total-assets" style={{ fontFamily: "Chivo, sans-serif" }}>
          {totalAssets.toLocaleString("it-IT")}
        </p>
        <p className="text-xs text-white/70 mt-1">Entità totali monitorate in {currentYear}</p>

        <div className="mt-4 space-y-1.5 pt-3 border-t border-white/[0.04]">
          <div className="flex justify-between text-[11px]"><span className="text-white/80">Salute media clienti</span><span className="tabular-nums font-bold text-[#00E676]">{healthGauges.client_health || 0}/100</span></div>
          <div className="flex justify-between text-[11px]"><span className="text-white/80">Salute media fornitori</span><span className="tabular-nums font-bold text-[#00E5FF]">{healthGauges.supplier_health || 0}/100</span></div>
          <div className="flex justify-between text-[11px]"><span className="text-white/80">Alert fornitori attivi</span><span className="tabular-nums font-bold text-[#FF1744]">{decliningSuppliers.length}</span></div>
        </div>

        {/* Donut chart */}
        <div className="mt-5 pt-4 border-t border-white/[0.04]">
          <p className="text-[10px] tracking-[0.22em] uppercase text-white font-bold mb-3">Distribuzione entità</p>
          <div className="flex items-center gap-4">
            <svg width="110" height="110" viewBox="0 0 110 110" data-testid="op-donut">
              {donut.map((d, i) => {
                const cx = 55, cy = 55, r = 42;
                const a1 = d.start * 2 * Math.PI - Math.PI / 2;
                const a2 = d.end * 2 * Math.PI - Math.PI / 2;
                const large = (d.end - d.start) > 0.5 ? 1 : 0;
                const x1 = cx + r * Math.cos(a1), y1 = cy + r * Math.sin(a1);
                const x2 = cx + r * Math.cos(a2), y2 = cy + r * Math.sin(a2);
                if (d.value === 0) return null;
                return (
                  <path key={i} d={`M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} Z`}
                    fill={d.color} stroke="#0D0F12" strokeWidth="2" />
                );
              })}
              <circle cx="55" cy="55" r="24" fill="#0D0F12" />
            </svg>
            <ul className="flex-1 space-y-1">
              {dist.map(d => (
                <li key={d.label} className="flex items-center justify-between text-[11px]">
                  <span className="flex items-center gap-1.5"><span className="w-2 h-2 inline-block" style={{ background: d.color }} />{d.label}</span>
                  <span className="text-white/80 tabular-nums">{((d.value / distTotal) * 100).toFixed(1)}%</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Top 5 recent */}
        <div className="mt-5 pt-4 border-t border-white/[0.04]" data-testid="op-top5">
          <p className="text-[10px] tracking-[0.22em] uppercase text-white font-bold mb-2">Ultime 5 aziende</p>
          <ul className="space-y-1">
            {(recentBusinesses || []).slice(0, 5).map((b, i) => (
              <li key={b.id || i} className="flex items-center justify-between text-[11px] gap-2">
                <span className="text-white/60 tabular-nums w-4">{i + 1}</span>
                <button onClick={() => onNav(`/sales/businesses`)} className="flex-1 text-left text-white truncate hover:text-[#00E5FF]">{b.name || "—"}</button>
                <span className="text-white/70 text-[10px] uppercase tracking-widest">{(b.sector || "").slice(0, 12)}</span>
              </li>
            ))}
            {(!recentBusinesses || recentBusinesses.length === 0) && <li className="text-white/50 text-[11px] italic">Nessuna azienda</li>}
          </ul>
        </div>
      </div>

      {/* CENTER column — hero title + KPI stack */}
      <div className="lg:col-span-6 bg-[#0D0F12] border border-white/[0.04] p-5" style={{ borderRadius: "2px" }} data-testid="openpolis-center">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-[10px] tracking-[0.22em] uppercase text-white font-bold">Dove si concentrano gli asset target</p>
            <p className="text-[10px] text-white/70 mt-0.5">Regione per regione, per il periodo corrente</p>
          </div>
          <div className="flex items-center gap-1" data-testid="op-year-tabs">
            {[currentYear, currentYear - 1, currentYear - 2].map(y => (
              <button key={y} onClick={() => setSelectedYear(y)}
                className={`text-[10px] tracking-widest uppercase font-bold px-2 py-1 transition-colors ${y === selectedYear ? "bg-[#00E5FF]/12 text-[#00E5FF] border border-[#00E5FF]/40" : "text-white/70 hover:text-white border border-transparent"}`}
                style={{ borderRadius: "2px" }} data-testid={`op-year-${y}`}>{y}</button>
            ))}
          </div>
        </div>

        <div className="mt-4 flex items-center gap-2 flex-wrap">
          <label className="text-[10px] tracking-widest uppercase text-white/70">Filtra paese:</label>
          <select value={selectedCountry} onChange={e => setSelectedCountry(e.target.value)}
            className="bg-[#0A0C10] border border-white/[0.08] px-2 py-1 text-xs text-white outline-none"
            style={{ borderRadius: "2px" }} data-testid="op-country-filter">
            <option value="">Tutti i paesi</option>
            {availableCountries.map(c => (
              <option key={c.country_code} value={c.country_code}>{c.country_name} ({c.total})</option>
            ))}
          </select>
          {selectedCountry && (
            <button onClick={() => setSelectedCountry("")} className="text-[10px] tracking-widest uppercase text-white/70 hover:text-white" data-testid="op-country-reset">Reset</button>
          )}
        </div>

        <div className="mt-4" data-testid="op-world-map-wrap">
          <WorldEntityMap year={selectedYear} country={selectedCountry} />
        </div>

        <p className="text-[10px] text-white/60 italic mt-5 pt-3 border-t border-white/[0.04]">
          Aggregato di clienti, fornitori, contatti e progetti R&D correntemente monitorati nella piattaforma Moonstone.
        </p>
      </div>

      {/* RIGHT column — monthly signal bars + sources */}
      <div className="lg:col-span-3 space-y-4">
        <div className="bg-[#0D0F12] border border-white/[0.04] p-5" style={{ borderRadius: "2px" }} data-testid="openpolis-monthly">
          <div className="flex items-center justify-between mb-3">
            <p className="text-[10px] tracking-[0.22em] uppercase text-white font-bold">Segnali per mese</p>
            <p className="text-[10px] text-white/70">contatti + conversioni</p>
          </div>
          <ul className="space-y-1.5">
            {monthlyRows.length === 0 && <li className="text-white/50 text-[11px] italic">Nessun dato metrics</li>}
            {monthlyRows.map((r, i) => (
              <li key={i} className="flex items-center gap-2 text-[11px]" data-testid={`op-month-${i}`}>
                <span className="w-16 text-white/80 truncate">{r.label}</span>
                <div className="flex-1 h-2 bg-white/[0.04] relative">
                  <div className="h-full bg-[#FF1744]" style={{ width: `${(r.value / maxMonthly) * 100}%` }} />
                </div>
                <span className="w-10 text-right tabular-nums text-white">{r.value}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="bg-[#0D0F12] border border-white/[0.04] p-5" style={{ borderRadius: "2px" }} data-testid="openpolis-sources">
          <p className="text-[10px] tracking-[0.22em] uppercase text-white font-bold mb-3">Da dove arrivano i numeri</p>
          <ul className="space-y-2 text-[11px]">
            <SourceRow label="Business List" value="Attiva" desc="Aziende tracciate" />
            <SourceRow label="People Search" value="Attiva" desc="Contatti profilati" />
            <SourceRow label="Supply Chain" value="Attiva" desc={`${decliningSuppliers.length} alert`} />
            <SourceRow label="Contract Twin" value="Attiva" desc="Clausole + esposizione" />
            <SourceRow label="Cyber Intelligence" value="Demo" desc="Shodan OSINT" />
          </ul>
          <button onClick={() => onNav("/settings")} className="mt-3 w-full text-[10px] tracking-widest uppercase font-bold px-3 py-2 border border-white/[0.08] text-white hover:bg-white/[0.04]" style={{ borderRadius: "2px" }} data-testid="op-sources-cta">
            Vedi tutte le fonti
          </button>
        </div>
      </div>
    </div>
  );
}

function StackKpi({ label, value, sub, color, onClick }) {
  const Comp = onClick ? "button" : "div";
  return (
    <Comp onClick={onClick} className={`text-left bg-white/[0.02] border border-white/[0.04] px-3 py-2.5 ${onClick ? "hover:bg-white/[0.06] transition-colors" : ""}`} style={{ borderRadius: "2px" }}>
      <p className="text-[9px] tracking-widest uppercase text-white/70">{label}</p>
      <p className="text-xl font-bold tabular-nums text-white mt-0.5" style={{ color, fontFamily: "Chivo, sans-serif" }}>{value}</p>
      <p className="text-[10px] text-white/60 mt-0.5">{sub}</p>
    </Comp>
  );
}

function SourceRow({ label, value, desc }) {
  const isActive = value === "Attiva";
  return (
    <li className="flex items-center justify-between gap-2 pb-2 border-b border-white/[0.03] last:border-0">
      <div className="min-w-0 flex-1">
        <p className="text-white truncate">{label}</p>
        <p className="text-white/60 text-[10px]">{desc}</p>
      </div>
      <span className={`text-[9px] tracking-widest uppercase font-bold px-1.5 py-0.5 ${isActive ? "bg-[#00E676]/12 text-[#00E676]" : "bg-[#FFB800]/12 text-[#FFB800]"}`} style={{ borderRadius: "2px" }}>
        {value}
      </span>
    </li>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   WorldEntityMap — vera mappa globale con heat markers per densità entità
   ═══════════════════════════════════════════════════════════════════ */
function WorldEntityMap({ year, country }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (year) params.year = year;
    if (country) params.country = country;
    axios.get(`${API}/dashboard/entity-density`, { params, withCredentials: true })
      .then(r => setRows(r.data?.rows || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [API, year, country]);

  const maxTotal = useMemo(() => Math.max(1, ...rows.map(r => r.total)), [rows]);
  const total = useMemo(() => rows.reduce((s, r) => s + r.total, 0), [rows]);

  const radiusFor = (v) => 5 + Math.round(22 * Math.sqrt(v / maxTotal));
  const colorFor = (v) => {
    const t = v / maxTotal;
    if (t > 0.66) return "#FF1744";
    if (t > 0.33) return "#FF8A00";
    return "#00E5FF";
  };

  return (
    <div className="bg-white/[0.02] border border-white/[0.04]" style={{ borderRadius: "2px" }} data-testid="op-world-map">
      <div className="flex items-center justify-between px-3 py-2 border-b border-white/[0.04]">
        <div className="flex items-center gap-2">
          <Globe className="w-4 h-4 text-[#00E5FF]" />
          <p className="text-[10px] tracking-widest uppercase font-bold text-white">Densità entità per paese</p>
        </div>
        <p className="text-[10px] text-white/70 tabular-nums">
          {rows.length} paesi · {total.toLocaleString("it-IT")} entità
        </p>
      </div>
      <div style={{ height: "420px", width: "100%", background: "#050505" }}>
        {loading ? (
          <p className="text-[11px] text-white/60 text-center py-24">Caricamento…</p>
        ) : rows.length === 0 ? (
          <p className="text-[11px] text-white/60 text-center py-24 italic">Nessun paese con entità da mostrare.</p>
        ) : (
          <MapContainer
            key={country || "world"}
            center={rows.length === 1 ? [rows[0].lat, rows[0].lon] : [30, 15]}
            zoom={rows.length === 1 ? 5 : 2}
            scrollWheelZoom={false}
            style={{ height: "100%", width: "100%", background: "#050505" }}
          >
            <TileLayer
              url="https://{s}.basemaps.cartocdn.com/dark_nolabels/{z}/{x}/{y}{r}.png"
              attribution='&copy; OSM &copy; CARTO'
            />
            {rows.map(r => (
              <CircleMarker
                key={r.country_code}
                center={[r.lat, r.lon]}
                radius={radiusFor(r.total)}
                pathOptions={{ color: colorFor(r.total), fillColor: colorFor(r.total), fillOpacity: 0.55, opacity: 0.9, weight: 1 }}
              >
                <Tooltip direction="top" opacity={1}>
                  <div style={{ fontFamily: "monospace", fontSize: "11px" }}>
                    <b>{r.country_name}</b><br />
                    Aziende: {r.businesses}<br />
                    Contatti: {r.people}<br />
                    <span style={{ color: "#FF1744" }}>Totale: {r.total}</span>
                  </div>
                </Tooltip>
              </CircleMarker>
            ))}
          </MapContainer>
        )}
      </div>
      <div className="flex items-center gap-3 px-3 py-2 border-t border-white/[0.04] text-[10px] text-white/70">
        <span className="flex items-center gap-1"><span className="w-2 h-2 inline-block rounded-full" style={{ background: "#00E5FF" }} />Bassa</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 inline-block rounded-full" style={{ background: "#FF8A00" }} />Media</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 inline-block rounded-full" style={{ background: "#FF1744" }} />Alta</span>
        <span className="ml-auto italic">Dimensione ∝ √(densità)</span>
      </div>
    </div>
  );
}

